import { useEffect, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import { omit, isEmpty } from 'lodash-es';
import { useQuery } from 'react-query';
import { asyncForEach, showError, showSuccess } from '@utils';
import { sellVehiclesServices } from '@services';
import { sellVehiclesEditPayload, sellVehiclesPayload } from '@helpers';
import { QUERY_KEYS } from 'constants/queryKeys';
import { URLS } from 'layouts/DrawerLayout/constants';
import { useServicePoints } from 'hooks/common';
import {
  handleEngineTypes,
  normalizeEnums,
  normalizeVehicle,
  handleSantanderModelOptions,
} from './utils';

export const useSellVehicleOperations = () => {
  const {
    query: { sellVehicleId, tab },
    push,
  } = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { servicePoints } = useServicePoints();

  const fetchEnums = async () => {
    const enums = await sellVehiclesServices?.getSellVehicleEnums();
    const colors = await sellVehiclesServices?.getSellVehicleColors();
    const providers = await sellVehiclesServices?.getSellVehicleProviders();
    const models = await sellVehiclesServices?.getSellVehiclesModels();
    const dealers = await sellVehiclesServices?.getSellVehicleDealers();

    return {
      ...enums?.data,
      colors,
      providers,
      models,
      dealers,
      servicePoints,
    };
  };

  const enumsResponse = useQuery([QUERY_KEYS.GET_SELL_VEHICLE_ENUMS_KEY], () =>
    fetchEnums()
  );

  const sellVehicleResponse = useQuery(
    [QUERY_KEYS.GET_SELL_VEHICLE_KEY, sellVehicleId],
    () => sellVehiclesServices?.getSellVehicle(sellVehicleId),
    {
      enabled:
        !!sellVehicleId || (sellVehicleId === null && enumsResponse.isSuccess),
    }
  );

  const vehicle = useMemo(
    () =>
      normalizeVehicle(sellVehicleResponse?.data?.data, enumsResponse?.data),
    [sellVehicleResponse?.data?.data, enumsResponse?.data]
  );

  const {
    data: santanderModelOptionsData,
    isLoading: isSantanderModelOptionsLoading,
    error: santanderModelOptionsError,
  } = useQuery(
    [QUERY_KEYS.GET_SANTANDER_MODEL_OPTIONS_KEY],
    () => {
      const brand = vehicle?.vehicle?.sku?.brandSlug;

      const model = vehicle?.vehicle?.sku?.modelSlug;

      const params = { brand, model };

      return sellVehiclesServices.getSellVehiclesSantanderOptions(params);
    },
    {
      refetchOnMount: true,
      enabled:
        !isEmpty(vehicle) &&
        !!vehicle?.vehicle?.sku?.brandSlug &&
        !!vehicle?.vehicle?.sku?.modelSlug,
    }
  );

  const santanderModelOptions = useMemo(
    () => handleSantanderModelOptions(santanderModelOptionsData?.data),
    [santanderModelOptionsData?.data]
  );

  const handleSubmitNewSellVehicle = async ({
    formValues,
    images,
    documents,
  }) => {
    try {
      setIsSubmitting(true);
      const form = sellVehiclesPayload(formValues);
      const primaryImageId =
        images?.filter((image) => image?.isPrimary)?.[0]?.id || null;
      images?.forEach((image) => {
        delete image?.isPrimary;
      });

      const payload = {
        images,
        form,
        docs: documents,
        primaryImageId,
      };

      if (form.initialPrice) {
        if (
          Number(form.initialPrice) > 0 &&
          Number(form.initialPrice) < Number(form.price)
        ) {
          return showError('Initial Price must be higher than Selling Price');
        }
      }
      const { skuId } = payload.form;

      await sellVehiclesServices.postSellVehicle(payload.form);

      if (payload.docs?.length) {
        await asyncForEach(payload?.docs, async (doc) => {
          sellVehiclesServices.postSellVehicleFile(skuId, doc.file);
        });
      }

      showSuccess('The vehicle is created successfully!');
      setTimeout(() => {
        push(URLS.sellVehicles);
      }, 1);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async ({ formValues, images, documents }) => {
    try {
      setIsSubmitting(true);

      const form = sellVehiclesEditPayload(formValues);
      const primaryImageId =
        images?.filter((image) => image?.isPrimary)?.[0]?.id || null;

      const cleanImages = images?.map((image) => {
        const cleanImage = omit(image, [
          'createdAt',
          'updatedAt',
          'url',
          'file',
          'filename',
          'uri',
          'isPrimary',
        ]);
        return cleanImage;
      });

      const payload = {
        images: cleanImages || [],
        form,
        docs: documents || [],
      };

      const { id } = payload.form;
      delete payload.form.id;
      const data = {
        ...payload.form,
        images: [...payload.images],
        mainImage: primaryImageId,
      };
      await sellVehiclesServices.updateSellVehicle(id, data);

      showSuccess('The vehicle is updated successfully!');
      setTimeout(() => {
        push(URLS.sellVehicles);
      }, 1);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (enumsResponse?.error) {
      showError(enumsResponse?.error);
    }
  }, [enumsResponse?.error]);

  const error = sellVehicleResponse?.error || santanderModelOptionsError;

  useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error]);

  const enums = normalizeEnums(enumsResponse?.data);

  const engineTypes = handleEngineTypes(enumsResponse?.data?.engineTypes);

  return {
    loading:
      sellVehicleResponse.isLoading ||
      enumsResponse.isLoading ||
      isSantanderModelOptionsLoading,
    vehicle,
    santanderModelOptions,
    tab,
    enums,
    enumsResponse,
    vehicleId: sellVehicleId,
    isSubmitting,
    engineTypes,
    getSellVehicle: sellVehicleResponse.refetch,
    handleSubmitNewSellVehicle,
    handleSubmit,
  };
};
